import { useRef } from 'react';
import { animate, utils } from 'animejs';
import { cn } from '@/lib/utils';
import { useAnimeScope } from '@/lib/motion';
import { AVATARES } from '@/lib/avatares';
import doctorSvg from '@/assets/avatares/doctor.svg?raw';
import doctoraSvg from '@/assets/avatares/doctora.svg?raw';

/*
 * Los SVG salen de doc/LOGO CONSALUD.ai con `node scripts/avatares-svg.mjs` y traen grupos
 * [data-parte="cabeza|ojos|brazo|mano|cuerpo"] con su pivote (transform-origin) ya puesto.
 * Solo hay una pose por personaje: el doctor saluda hacia la derecha y la doctora hacia la
 * izquierda; las demás variantes de AVATARES se obtienen reflejándolos.
 */
type Personaje = 'doctor' | 'doctora';
const SVG: Record<Personaje, string> = { doctor: doctorSvg, doctora: doctoraSvg };
const VARIANTES: Record<string, { personaje: Personaje; espejo: boolean }> = {
  [AVATARES.doctor]: { personaje: 'doctor', espejo: false },
  [AVATARES.doctorIzq]: { personaje: 'doctor', espejo: false },
  [AVATARES.doctorSenala]: { personaje: 'doctor', espejo: true },
  [AVATARES.doctora]: { personaje: 'doctora', espejo: true },
  [AVATARES.doctoraIzq]: { personaje: 'doctora', espejo: true },
  [AVATARES.doctoraSenala]: { personaje: 'doctora', espejo: false },
};

/**
 * Mascota (doctor / doctora) animada: respira, mueve un poco la cabeza, parpadea y cada
 * tantos segundos saluda con la mano. Con prefers-reduced-motion queda estática.
 * `src` es una de AVATARES; `className` posiciona y da la altura (p. ej. "absolute h-28").
 */
export function Avatar({ src, className }: { src: string; className?: string }) {
  const wrap = useRef<HTMLSpanElement>(null);
  const variante = VARIANTES[src];

  useAnimeScope(
    wrap,
    (reduce) => {
      if (reduce || !variante) return;
      const root = wrap.current!;
      const svg = root.querySelector('svg')!;
      const partes = (p: string) => root.querySelectorAll<SVGGElement>(`[data-parte="${p}"]`);
      // Desfase aleatorio para que varias mascotas en pantalla no se muevan al unísono.
      const delay = utils.random(0, 1200);
      // En el marco del SVG el brazo del doctor sube girando a la izquierda y el de la doctora a la derecha.
      const sube = variante.personaje === 'doctor' ? -1 : 1;

      utils.set(svg, { transformOrigin: '50% 100%' });
      animate(svg, {
        scaleY: [1, 1.02],
        duration: 1800,
        delay,
        ease: 'inOutSine',
        loop: true,
        alternate: true,
      });
      animate(partes('cabeza'), {
        rotate: [-2, 2],
        duration: 2600,
        delay,
        ease: 'inOutSine',
        loop: true,
        alternate: true,
      });

      const ojos = partes('ojos');
      utils.set(ojos, { transformBox: 'fill-box', transformOrigin: '50% 50%' });
      animate(ojos, {
        scaleY: [
          { to: 0.1, duration: 70 },
          { to: 1, duration: 120 },
        ],
        ease: 'inOutQuad',
        delay: delay + 1500,
        loop: true,
        loopDelay: utils.random(2800, 4200),
      });

      // Saludo: el brazo sube y la mano se agita. Mismo periodo en ambos (duración + loopDelay)
      // para que sigan sincronizados en cada repetición.
      const inicio = 300 + delay / 3;
      const pausa = utils.random(6000, 9000);
      animate(partes('brazo'), {
        rotate: [
          { to: 6 * sube, duration: 250, ease: 'outQuad' },
          { to: 6 * sube, duration: 1000 },
          { to: 0, duration: 400, ease: 'inOutQuad' },
        ],
        delay: inicio,
        loop: true,
        loopDelay: pausa,
      });
      animate(partes('mano'), {
        rotate: [
          { to: -16 * sube, duration: 220 },
          { to: 12 * sube, duration: 260 },
          { to: -12 * sube, duration: 260 },
          { to: 8 * sube, duration: 260 },
          { to: 0, duration: 220 },
        ],
        ease: 'inOutSine',
        delay: inicio + 150,
        loop: true,
        loopDelay: pausa + 1650 - 1220,
      });
    },
    [src],
  );

  if (!variante) {
    return (
      <img
        src={src}
        alt=""
        draggable={false}
        className={cn('pointer-events-none select-none', className)}
      />
    );
  }
  return (
    <span
      ref={wrap}
      aria-hidden="true"
      className={cn('pointer-events-none block select-none', className)}
    >
      <span
        className={cn(
          'block h-full [&>svg]:h-full [&>svg]:w-auto [&>svg]:overflow-visible',
          variante.espejo && '-scale-x-100',
        )}
        dangerouslySetInnerHTML={{ __html: SVG[variante.personaje] }}
      />
    </span>
  );
}
